// components/formDialog/AlumniDialog.tsx
import AsyncSingleSelectField from "@/components/form/components/AsyncSingleSelectField";
import InputField from "@/components/form/components/InputField";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";
import {
  useCreateAlumniMutation,
  useUpdateAlumniMutation,
} from "@/hooks/api/alumni.queries";
import {
  AlumniCreateType,
  AlumniParamsType,
  AlumniType,
} from "@/services/alumni.service";
import { batchService } from "@/services/batch.service";
import { departmentService } from "@/services/department.service";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm, useWatch } from "react-hook-form";
import * as z from "zod";

type AlumniDialogProps = {
  mode: "create" | "edit";
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  filters: AlumniParamsType;
  alumniId?: string;
  alumniData?: AlumniType | null;
  onCancel: () => void;
};

const alumniSchema = z.object({
  name: z.string().min(1, "Full Name is required"),
  rollNo: z.string().min(1, "Roll Number is required"),
  email: z.string().email("Invalid email address").min(1, "Email is required"),
  phone: z.string().optional(),
  dept: z.string().min(1, "Department is required"),
  batch: z.string().min(1, "Batch is required"),
  graduationYear: z.string().min(1, "Graduation Year is required"),
  company: z.string().optional(),
  position: z.string().optional(),
  location: z.string().optional(),
  experience: z.number().min(0, "Experience cannot be negative").optional(),
  linkedIn: z.string().optional(),
});

type AlumniFormType = z.infer<typeof alumniSchema>;

const AlumniDialog = ({
  mode,
  isOpen,
  onOpenChange,
  filters,
  alumniId,
  alumniData,
  onCancel,
}: AlumniDialogProps) => {
  const createMutation = useCreateAlumniMutation(filters);
  const updateMutation = useUpdateAlumniMutation(filters);
  const isPending = createMutation.isPending || updateMutation.isPending;

  const form = useForm<AlumniFormType>({
    resolver: zodResolver(alumniSchema),
    defaultValues: {
      name: "",
      rollNo: "",
      email: "",
      phone: "",
      dept: "",
      batch: "",
      graduationYear: "",
      company: "",
      position: "",
      location: "",
      experience: undefined,
      linkedIn: "",
    },
  });

  const selectedDepartment = useWatch({
    control: form.control,
    name: "dept",
  });

  useEffect(() => {
    if (isOpen) {
      if (mode === "edit" && alumniData) {
        setTimeout(() => {
          form.reset({
            name: alumniData.name || "",
            rollNo: alumniData.rollNo || "",
            email: alumniData.email || "",
            phone: alumniData.phone || "",
            dept: alumniData.dept || "",
            batch: alumniData.batch || "",
            graduationYear: alumniData.graduationYear || "",
            company: alumniData.company || "",
            position: alumniData.position || "",
            location: alumniData.location || "",
            experience: alumniData.experience,
            linkedIn: alumniData.linkedIn || "",
          });
        }, 0);
      } else {
        form.reset({
          name: "",
          rollNo: "",
          email: "",
          phone: "",
          dept: "",
          batch: "",
          graduationYear: "",
          company: "",
          position: "",
          location: "",
          experience: undefined,
          linkedIn: "",
        });
      }
    }
  }, [mode, alumniData, isOpen, form]);

  const handleSubmit = (values: AlumniFormType) => {
    const submissionData: AlumniCreateType = {
      ...values,
      status: alumniData?.status || "active", // Keep existing status or default to active
    };

    if (mode === "create") {
      createMutation.mutate(submissionData, { onSuccess: onCancel });
    } else if (mode === "edit" && alumniId) {
      updateMutation.mutate(
        { _id: alumniId, data: submissionData },
        { onSuccess: onCancel }
      );
    }
  };

  const defaultDeptOptions = alumniData?.dept
    ? [{ label: `Dept ${alumniData.dept}`, value: alumniData.dept }]
    : [];

  const defaultBatchOptions = alumniData?.batch
    ? [{ label: `Batch ${alumniData.batch}`, value: alumniData.batch }]
    : [];

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[90vh] overflow-y-auto sm:max-w-xl"
        onInteractOutside={(e) => {
          e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>
            {mode === "create" ? "Add New Alumni" : "Edit Alumni"}
          </DialogTitle>
          <p className="text-muted-foreground mt-1 text-sm">
            Fill in the details to{" "}
            {mode === "create" ? "add a new alumni" : "edit the alumni"}
          </p>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="grid grid-cols-1 gap-4 py-2 md:grid-cols-12"
          >
            {/* Row 1: Name, Roll */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:col-span-12">
              <InputField<AlumniFormType>
                type="text"
                name="name"
                label="Full Name"
                placeholder="e.g. Michael Chen"
                required
                disabled={isPending}
              />
              <InputField<AlumniFormType>
                type="text"
                name="rollNo"
                label="Roll Number"
                placeholder="e.g. CSE2019012"
                required
                disabled={isPending}
              />
            </div>

            {/* Row 2: Email and Phone */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:col-span-12">
              <InputField<AlumniFormType>
                type="email"
                name="email"
                label="Email"
                placeholder="e.g. michael.chen@gmail.com"
                required
                disabled={isPending}
              />
              <InputField<AlumniFormType>
                type="text"
                name="phone"
                label="Phone Number"
                placeholder="e.g. +1-555-0201"
                disabled={isPending}
              />
            </div>

            {/* Row 3: Department and Batch */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:col-span-12">
              <AsyncSingleSelectField<AlumniFormType>
                name="dept"
                label="Department"
                placeholder="Select department"
                fetchOptions={departmentService.fetchDepartmentOptions}
                queryKey={["departmentsDrop"]}
                defaultOptions={defaultDeptOptions}
                required
                disabled={isPending}
              />
              <AsyncSingleSelectField<AlumniFormType>
                name="batch"
                label="Batch"
                placeholder={
                  selectedDepartment
                    ? "Select batch"
                    : "Select department first"
                }
                fetchOptions={batchService.fetchBatchOptions}
                queryKey={["batchesDrop", selectedDepartment || "null"]}
                defaultOptions={defaultBatchOptions}
                required
                disabled={isPending || !selectedDepartment}
              />
            </div>

            {/* Row 4: Graduation Year */}
            <div className="md:col-span-12">
              <InputField<AlumniFormType>
                type="text"
                name="graduationYear"
                label="Graduation Year"
                placeholder="e.g., 2023"
                required
                disabled={isPending}
              />
            </div>

            {/* Row 5: Company, Position */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:col-span-12">
              <InputField<AlumniFormType>
                type="text"
                name="company"
                label="Current Company"
                placeholder="e.g., Google Inc."
                disabled={isPending}
              />
              <InputField<AlumniFormType>
                type="text"
                name="position"
                label="Position/Designation"
                placeholder="e.g., Software Engineer"
                disabled={isPending}
              />
            </div>

            {/* Row 6: Location, Experience */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:col-span-12">
              <InputField<AlumniFormType>
                type="text"
                name="location"
                label="Location"
                placeholder="e.g., San Francisco, CA"
                disabled={isPending}
              />
              <InputField<AlumniFormType>
                type="number"
                name="experience"
                label="Total Experience (years)"
                placeholder="e.g., 3"
                disabled={isPending}
              />
            </div>

            {/* Row 7: LinkedIn */}
            <div className="md:col-span-12">
              <InputField<AlumniFormType>
                type="url"
                name="linkedIn"
                label="LinkedIn Profile URL"
                placeholder="https://linkedin.com/in/username"
                disabled={isPending}
              />
            </div>

            <DialogFooter className="mt-4 md:col-span-12">
              <Button
                type="button"
                variant="outline"
                onClick={onCancel}
                disabled={isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending
                  ? mode === "create"
                    ? "Adding..."
                    : "Saving..."
                  : mode === "create"
                    ? "Create Alumni"
                    : "Save Changes"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default AlumniDialog;
